/* eslint-disable @typescript-eslint/no-unused-vars */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import {
  Authenticated,
  BaseUrl,
  Body,
  Delete,
  Get,
  Head,
  Header,
  Patch,
  PathParam,
  Post,
  Put,
  QueryParam,
  UploadCallback
} from './decorators';
import { endpoint } from './endpoint';
import type { CredReqEventStatus } from '$shared/constants/CredReqEventStatus';
import type { CredReqPriority } from '$shared/constants/CredReqPriority';
import type { CredReqSortBy } from '$shared/constants/CredReqSortBy';
import type { CredReqType } from '$shared/constants/CredReqType';
import type { CredentialingRequestSourceType } from '$shared/constants/CredentialingRequestSourceType';
import type { CvoEntityType } from '$shared/constants/CredReqEntityType';
import type { ICredReq } from '$shared/interfaces/api/ICredReq';
import type { IPageRes } from '$shared/interfaces/api/IPageRes';
import type { IPatchCredReq } from '$shared/interfaces/api/IPatchCredReq';
import type { ICountryModel } from '$shared/interfaces/api/ICountryModel';
import type { IClientConfig } from '$shared/interfaces/api/IClientConfig';
import type CacheControl from '$shared/constants/CacheControl';
import type { ISchemaMeta } from '$shared/interfaces/api/ISchemaMeta';
import type { IPasswordAuth } from '$shared/interfaces/api/IPasswordAuth';
import type { IAuthorization } from '$shared/interfaces/api/IAuthorization';
import type { AuthTokenSort } from '$shared/constants/AuthTokenSort';
import type SortDirection from '$shared/constants/SortDirection';
import type { IAuthToken } from '$shared/interfaces/api/IAuthToken';
import type { IGoogleAuth } from '$shared/interfaces/api/IGoogleAuth';
import type { IForgotPassword } from '$shared/interfaces/api/IForgotPassword';
import type { IResetPassword } from '$shared/interfaces/api/IResetPassword';
import type { Role } from '$shared/constants/Role';
import type { IUserRole } from '$shared/interfaces/api/IUserRole';
import type { IUser } from '$shared/interfaces/api/IUser';
import type { DatasetJobStatus } from '$shared/constants/DatasetJobStatus';
import type { Trigger } from '$shared/constants/Trigger';
import type { IDatasetScan } from '$shared/interfaces/api/IDatasetScan';
import type { IStartDatasetScan } from '$shared/interfaces/api/IStartDatasetScan';
import type { IOrgSettings } from '$shared/interfaces/api/IOrgSettings';
import type { IFile } from '$shared/interfaces/IFile';
import type { FileSortBy } from '$shared/constants/FileSortBy';
import type ApiError from '$shared/models/api/ApiError';
import type { IMonitorReq } from '$shared/interfaces/api/IMonitorReq';
import type { IMonitor } from '$shared/interfaces/api/IMonitor';
import type MonitorType from '$provider/lib/apis/models/MonitorType';
import type { MonitorSort } from '$shared/constants/MonitorSort';
import type { IMonitorPatchReq } from '$shared/interfaces/api/IMonitorPatchReq';
import type { UserActionResolution } from '$shared/constants/UserActionResolution';
import type { MatchRelevance } from '$shared/constants/MatchRelevance';
import type { DatasetMatchSortBy } from '$shared/constants/DatasetMatchSortBy';
import type { IDatasetMatch } from '$shared/interfaces/api/IDatasetMatch';
import type { IDatasetMatchPatchRequest } from '$shared/interfaces/api/IDatasetMatchPatchRequest';
import type { DatasetTag } from '$shared/constants/DatasetTag';
import type DatasetMetaSort from '$shared/constants/DatasetMetaSort';
import type { IDatasetMeta } from '$shared/interfaces/api/IDatasetMeta';
import type { ModuleEntityType } from '$shared/constants/ModuleEntityType';
import type { ICheckList } from '$shared/interfaces/api/ICheckList';
import type { IAttachCredReqEvent } from '$shared/interfaces/api/IAttachCredReqEvent';
import type { IAuditLog } from '$shared/interfaces/api/IAuditLog';
import type AuditLogsSort from '$shared/constants/AuditLogsSort';
import type DownloadLogsSort from '$shared/constants/DownloadLogsSort';
import type { IDownloadLog } from '$shared/interfaces/api/IDownloadLog';
import type { IClientSecretModel } from '$shared/interfaces/api/IClientSecretModel';
import type { IOAuthCredentialsModel } from '$shared/interfaces/api/IOAuthCredentialsModel';
import type { SimplifiedLicenseTypeStatusSort } from '$shared/constants/SimplifiedLicenseTypeStatusSort';
import type { ISimplifiedLicenseTypeStatus } from '$shared/interfaces/api/ISimplifiedLicenseTypeStatus';
import type { ISegment } from '$shared/interfaces/api/ISegment';
import type { TaxonomyGroupSort } from '$shared/constants/TaxonomyGroupSort';
import type { ITaxonomyGroup } from '$shared/interfaces/api/ITaxonomyGroup';
import type { TaxonomySort } from '$shared/constants/TaxonomySort';
import type { ITaxonomy } from '$shared/interfaces/api/ITaxonomy';
import type { ICreateCredReq } from '$shared/interfaces/api/ICreateCredReq';
import type { Settings } from '$shared/constants/Settings';

class ApiDefinition implements IClientConfig {
  protected authToken: string | null = null;
  protected orgId: string | null = null;

  public baseUrl: string = endpoint.get();
  public atlasBaseUrl: string = endpoint.getAtlasEndpoint();

  public get accessToken(): string {
    return this.authToken || '';
  }

  public get xImpersonateOrg(): string {
    return this.orgId || '';
  }

  public get checklistPutRequestUrl(): string {
    return `${endpoint.get()}credentialing-requests/:credReqId/checklist`;
  }

  public setAccessToken(acessToken: string): void {
    this.authToken = acessToken;
  }

  public setOrgId(orgId: string): void {
    this.orgId = orgId;
  }

  public clearOrgId(): void {
    this.orgId = null;
  }

  public clearAccessToken(): void {
    this.authToken = null;
  }

  //#region Auth
  @Post('auth/token/password')
  authPasswordAsync(@Body authInfo: IPasswordAuth): Promise<IAuthorization> {
    // API method will be empty
  }

  @Get('auth/token')
  getTokensAsync(
    @QueryParam('sortedBy') sortedBy: AuthTokenSort | undefined,
    @QueryParam('cursor') cursor: string | undefined,
    @QueryParam('sortDirection') sortDirection: SortDirection | undefined
  ): Promise<IPageRes<IAuthToken>> {
    // API method will be empty
  }

  @Post('auth/token/google')
  authGoogleAsync(@Body authInfo: IGoogleAuth): Promise<IResponse.IAuthorization> {
    // API method will be empty
  }

  @Post('internal/console/auth/token/google')
  consoleAuthGoogleAsync(@Body authInfo: IGoogleAuth): Promise<IResponse.IAuthorization> {
    // API method will be empty
  }

  @Post('auth/password/requestreset')
  forgotPasswordAsync(@Body authInfo: IForgotPassword): Promise<void> {
    // API method will be empty
  }
  @Post('auth/password/reset')
  resetPasswordAsync(@Body authInfo: IResetPassword): Promise<void> {
    // API method will be empty
  }

  @Authenticated()
  @Post('auth/token/:tokenId/invalidate')
  invalidateAccessTokenAsync(@PathParam('tokenId') tokenId: string): Promise<void> {
    // API method will be empty
  }

  @Post('auth/token/oauth')
  authOAuthAsync(@Body authInfo: IOAuthModel): Promise<IResponse.IAuthorization> {
    // API method will be empty
  }

  @Get('auth/sso/admin')
  getSSOAdminLinkAsync(@QueryParam('code') code: string): Promise<{ adminPortalLink: string }> {
    // API method will be empty
  }
  //endregion

  //#region User Role
  @Authenticated()
  @Get('organization/roles/:role')
  getOrganizationRoleAsync(@PathParam('role') role: Role): Promise<IUserRole> {
    // API method will be empty
  }
  //#endregion

  //#region Schemas
  @Authenticated()
  @Get('internal/schemas/:enumName')
  getSchemaMetadataAsync(@PathParam('enumName') enumName: string): Promise<ISchemaMeta[]> {
    // API method will be empty
  }
  //#endregion

  //#region Credentialing Requests
  @Authenticated()
  @Get('credentialing-requests/:id')
  getCredentialingRequestAsync(@PathParam('id') id: string): Promise<ICredReq> {
    // API method will be empty
  }

  @Authenticated()
  @Post('credentialing-requests')
  createCredentialingRequestAsync(@Body payload: ICreateCredReq): Promise<ICredReq> {
    // API method will be empty
  }

  @Authenticated()
  @Patch('credentialing-requests/:id')
  updateCredentialingRequestAsync(
    @PathParam('id') id: string,
    @Body payload: IPatchCredReq
  ): Promise<ICredReq> {
    // API method will be empty
  }

  @Authenticated()
  @Post('credentialing-requests/:id/events')
  attachCredReqEventAsync(
    @PathParam('id') id: string,
    @Body payload: IAttachCredReqEvent
  ): Promise<ICredReqEvent> {
    // API method will be empty
  }

  @Authenticated()
  @Get('credentialing-requests')
  getCredentialingRequestListAsync(
    @QueryParam('entityType') entityType: CvoEntityType,
    @QueryParam('currentEventStatus') currentEventStatus: CredReqEventStatus[] | null,
    @QueryParam('providerCredentialingStatus') providerCredentialingStatus: string | null,
    @QueryParam('priority') priority: CredReqPriority | null,
    @QueryParam('ownerId') ownerId: string[] | null,
    @QueryParam('includeUnassigned') includeUnassigned: boolean | null,
    @QueryParam('credentialingRequestType') credentialingRequestType: CredReqType | null = null,
    @QueryParam('entityId') entityId: string | null,
    @QueryParam('entityName') entityName: string | null,
    @QueryParam('entityNpi') entityNpi: number | null,
    @QueryParam('entityDeactivated') entityDeactivated: boolean | null = null,
    @QueryParam('facilitySpecialtyId') facilitySpecialtyId: string | null = null,
    @QueryParam('sourceType') sourceType: CredentialingRequestSourceType | null = null,
    @QueryParam('sortedBy') sortedBy: CredReqSortBy | null,
    @QueryParam('sort') sort: Sort<CredReqSortBy> | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number,
    @QueryParam('sortDirection') sortDirection: SortDirection
  ): Promise<IPageRes<ICredReq>> {
    // API method will be empty
  }

  @Authenticated()
  @Put('credentialing-requests/:credReqId/checklist')
  updateChecklistAsync(
    @PathParam('credReqId') credReqId: string,
    @Body payload: ICheckList
  ): Promise<void> {
    // API method will be empty
  }
  //#endregion

  //#region Segments
  @Authenticated()
  @Get('organization/segments')
  getSegmentListAsync(): Promise<ISegment[]> {
    // API method will be empty
  }
  //#endregion

  //#region Countries
  @Authenticated()
  @Get('countries')
  getCountriesListAsync(): ICountryModel[] {
    // API method will be empty
  }
  //#endregion

  //#region User
  @Authenticated()
  @Get('/users')
  getUsersAsync(): Promise<Array<IUser>> {
    // API method will be empty
  }
  //#endregion

  //#region Cred
  @Post('internal/credentialing/auth/token/google')
  credAuthGoogleAsync(@Body authInfo: IRequest.IGoogleAuth): Promise<IAuthorization> {
    // API method will be empty
  }

  @Get('internal/credentialing/grants')
  listCredOrganisationAsync(): Promise<ICredOrg[]> {
    // API method will be empty
  }

  @Authenticated()
  @Post('internal/credentialing/auth/token/:tokenId/invalidate')
  invalidateCredAccessTokenAsync(@PathParam('tokenId') tokenId: string): Promise<void> {
    // API method will be empty
  }

  @Authenticated()
  @Get('internal/credentialing/users')
  getCredUsersAsync(): Promise<Array<IUser>> {
    // API method will be empty
  }
  //#endregion

  //#region Datasets
  @Authenticated()
  @Get('datasets/scans')
  getDatasetScansListAsync(
    @QueryParam('type') type: string | string[] | null,
    @QueryParam('parameterSetId') parameterSetId: string | null,
    @QueryParam('referenceId') referenceId: string | null,
    @QueryParam('status') status: DatasetJobStatus | DatasetJobStatus[] | null,
    @QueryParam('includeOldScans') includeOldScans = false,
    @QueryParam('hasMonitor') hasMonitor: boolean | null,
    @QueryParam('trigger') trigger: Trigger | null,
    @QueryParam('entityType') entityType: string | null,
    @QueryParam('entityId') entityId: string | null,
    @QueryParam('entityName') entityName: string | null,
    @QueryParam('entityNpi') entityNpi: number | null,
    @QueryParam('entityDeactivated') entityDeactivated: boolean | null = null,
    @QueryParam('credentialingStatus') credentialingStatus: string | null,
    @QueryParam('sortedBy') sortedBy: DatasetSortBy | null,
    @QueryParam('sort') sort: Sort<DatasetSortBy> | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number | null,
    @QueryParam('sortDirection') sortDirection: SortDirection | null
  ): Promise<IPageRes<IDatasetScan>> {
    // API method will be empty
  }

  @Authenticated()
  @Get('datasets/scans/:scanId')
  getDatasetScanAsync(@PathParam('scanId') scanId: string): Promise<IDatasetScan> {
    // API method will be empty
  }

  @Authenticated()
  @Post('datasets/scans')
  startDatasetScanAsync(@Body payload: IStartDatasetScan): Promise<IDatasetScan> {
    // API method will be empty
  }

  @Authenticated()
  @Post('datasets/scans/:scanId/refresh')
  refreshDatasetScanAsync(@PathParam('scanId') scanId: string): Promise<void> {
    // API method will be empty
  }

  @Authenticated()
  @Get('datasets/matches')
  getDatasetMatchesListAsync(
    @QueryParam('types') types: string[] | null,
    @QueryParam('userActionNeeded') userActionNeeded: boolean | null,
    @QueryParam('referenceId') referenceId: string | null,
    @QueryParam('includeOldMatches') includeOldMatches: boolean | null,
    @QueryParam('userActionResolution') userActionResolution: UserActionResolution | null,
    @QueryParam('matchRelevance') matchRelevance: MatchRelevance[] | null,
    @QueryParam('entityType') entityType: string | null,
    @QueryParam('entityId') entityId: string | null,
    @QueryParam('entityName') entityName: string | null,
    @QueryParam('entityNpi') entityNpi: number | null,
    @QueryParam('entityDeactivated') entityDeactivated: boolean | null = null,
    @QueryParam('credentialingStatus') credentialingStatus: string[] | null,
    @QueryParam('sortedBy') sortedBy: DatasetMatchSortBy | null,
    @QueryParam('sort') sort: Sort<DatasetMatchSortBy> | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number | null,
    @QueryParam('sortDirection') sortDirection: SortDirection | null
  ): Promise<IPageRes<IDatasetMatch>> {
    // API method will be empty
  }

  @Authenticated()
  @Get('datasets/:datasetType')
  getDatasetMetadataAsync(@PathParam('datasetType') type: string): Promise<IDatasetMeta> {
    // API method will be empty
  }

  @Authenticated()
  @Patch('datasets/matches/:id')
  patchDatasetMatchAsync(
    @PathParam('id') matchId: string,
    @Body payload: IDatasetMatchPatchRequest
  ): Promise<IDatasetMatch> {
    // Api method will be empty
  }

  @Authenticated()
  @Get('datasets')
  getDatasetMetaListAsync(
    @QueryParam('tags') tags: DatasetTag[],
    @QueryParam('entityTypes') entityTypes: ModuleEntityType[],
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number,
    @QueryParam('sortedBy') sortedBy: DatasetMetaSort | null,
    @QueryParam('sortDirection') sortDirection: SortDirection
  ): Promise<IPageRes<IDatasetMeta>> {
    // API method will be empty
  }

  @Authenticated()
  @Post('datasets/:datasetType/uploads')
  uploadDatasetFileAsync(
    @PathParam('datasetType') datasetType: string,
    @Body formData: FormData
  ): Promise<void> {
    // Api method will be empty
  }
  //#endregion

  //#region Verification meta datas
  @Authenticated()
  @Get('internal/verifications/metadata')
  getVerificationMetaDataAsync(): Promise<IVerificationMetadata> {
    // API method will be empty
  }
  //#endregion

  //#region Not doccumented APIs
  @Authenticated()
  @Get('organization')
  getOrgSettingsAsync(): Promise<IOrgSettings> {
    // API method will be empty
  }

  @Authenticated()
  @Put('organization/settings/:setting')
  setOrganizationSettingsAsync<T>(
    @PathParam('setting') setting: string,
    @Body settingData: T
  ): Promise<void> {
    // API method will be empty
  }

  @Authenticated()
  @Get('organization/settings/:setting')
  getOrganizationSettingAsync<T>(
    @PathParam('setting') setting: string,
    @QueryParam('includeSensitiveInfo') includeSensitiveInfo?: boolean
  ): Promise<T | undefined> {
    // API method will be empty
  }

  @Authenticated()
  @Get('organization/settings/oauth-credentials')
  getOAuthCredentialsAsync(): Promise<IOAuthCredentialsModel[]> {
    // API method will be empty
  }

  @Authenticated()
  @Post('organization/settings/oauth-credentials/:clientId/client-secret')
  addClientSecretAsync(@PathParam('clientId') clientId: string): Promise<IClientSecretModel> {
    // API method will be empty
  }

  @Authenticated()
  @Delete('organization/settings/oauth-credentials/:clientId/client-secret/:clientSecretId')
  deleteClientSecretAsync(
    @PathParam('clientId') clientId: string,
    @PathParam('clientSecretId') clientSecretId: string
  ): Promise<void> {
    // API method will be empty
  }

  @Authenticated()
  @Get('organization/sso/admin')
  getSsoAdminPortalAsync(): Promise<string> {
    // API method will be empty
  }

  //#endregion

  //#region Files
  @Authenticated()
  @Get('files/uploads')
  getFileListAsync(
    @QueryParam('prefix') prefix: string | null,
    @QueryParam('cursor') cursor?: string | null,
    @QueryParam('count') count?: number | null,
    @QueryParam('sortedBy') sortedBy?: FileSortBy | null,
    @QueryParam('sortDirection') sortDirection?: SortDirection | null
  ): Promise<IPageRes<IFile>> {
    // API method will be empty
  }

  @Authenticated()
  @Delete('files/uploads/:path')
  deleteFileAsync(@PathParam('path') path: string): Promise<void> {
    // API method will be empty
  }

  @Authenticated()
  @Post('files/uploads/:path')
  renameFileAsync(
    @PathParam('path') path: string,
    @Header('x-source-path') oldPath: string,
    @Header('x-source-delete') isMove: boolean
  ): Promise<void> {
    // API method will be empty
  }

  /**
   * Downloads a file from the server
   * @param path - The file path to download
   * @param cache - Cache-Control header value to control caching behavior
   * @returns A Blob containing the file data
   */
  @Authenticated()
  @Get('files/:path')
  getFileAsync(
    @PathParam('path') path: string,
    @Header('Cache-Control') cache?: CacheControl
  ): Promise<Blob> {
    // API method will be empty
  }

  @Authenticated()
  @Post('files/uploads/:path')
  uploadFileAsync(
    @PathParam('path') path: string,
    @Body formData: FormData,
    @UploadCallback
    uploadCallback: (
      completed: number,
      total: number,
      requestFinished: boolean,
      error: ApiError
    ) => void
  ): Promise<Blob> {
    // API method will be empty
  }
  //#endregion

  //#region Monitoring
  @Authenticated()
  @Post('monitors')
  createMonitoringAsync(@Body payload: IMonitorReq): Promise<IMonitor> {
    // API method will be empty
  }

  @Authenticated()
  @Get('monitors')
  getMonitoringListAsync(
    @QueryParam('entityIds') entityIds: string[] | null,
    @QueryParam('entityType') entityType: string | null,
    @QueryParam('type') type: MonitorType | null,
    @QueryParam('datasetTypes') datasetTypes: string[] | null,
    @QueryParam('licenseIds') licenseIds: string[] | null,
    @QueryParam('sortedBy') sortedBy: MonitorSort | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number | null,
    @QueryParam('sortDirection') sortDirection: SortDirection | null
  ): Promise<IPageRes<IMonitor>> {
    // API method will be empty
  }

  @Authenticated()
  @Get('monitors/:id')
  getMonitoringAsync(@PathParam('id') id: string): Promise<IMonitor> {
    // API method will be empty
  }

  @Authenticated()
  @Patch('monitors/:id')
  patchMonitoringAsync(
    @PathParam('id') id: string,
    @Body payload: IMonitorPatchReq
  ): Promise<IMonitor> {
    // API method will be empty
  }

  @Authenticated()
  @Delete('monitors/:id')
  deleteMonitoringAsync(@PathParam('id') id: string): Promise<void> {
    // API method will be empty
  }
  //#endregion

  //#region Credentialing Request Aggregation
  @Authenticated()
  @Get('credentialing-requests/aggregations')
  getCredentialingReqAggregationAsync(
    @QueryParam('entityType') entityType: ModuleEntityType | null = null,
    @QueryParam('entityDeactivated') entityDeactivated: boolean | null = null
  ): Promise<ICredentialingReqAggregation> {
    // API method will be empty
  }
  //#endregion

  //#region AuditLog
  @Authenticated()
  @Get('log/events')
  getAuditLogsAsync(
    @QueryParam('entityId') entityId: string[] | null,
    @QueryParam('before') before: string | null,
    @QueryParam('after') after: string | null,
    @QueryParam('beforeInclusive') beforeInclusive: boolean | null,
    @QueryParam('afterInclusive') afterInclusive: boolean | null,
    @QueryParam('type') type: EventLogType[] | null,
    @QueryParam('category') category: EventLogCategory[] | null,
    @QueryParam('subcategory') subcategory: string[] | null,
    @QueryParam('sortDirection') sortDirection: SortDirection,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number,
    @QueryParam('SortedBy') sortedBy: AuditLogsSort | null,
    @QueryParam('sort') sort: Sort<AuditLogsSort> | null,
    @QueryParam('includeFailedRequests') includeFailedRequests: boolean
  ): Promise<IPageRes<IAuditLog>> {
    // API method will be empty
  }

  @Authenticated()
  @Get('files/exports')
  getDownloadLogs(
    @QueryParam('prefix') prefix: string | null,
    @QueryParam('before') before: string | null,
    @QueryParam('after') after: string | null,
    @QueryParam('beforeInclusive') beforeInclusive: boolean | null,
    @QueryParam('afterInclusive') afterInclusive: boolean | null,
    @QueryParam('sortDirection') sortDirection: SortDirection,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number,
    @QueryParam('sortedBy') sortedBy: DownloadLogsSort | null
  ): Promise<IPageRes<IDownloadLog>> {
    // API method will be empty
  }
  //#endregion

  //#region License Source
  @Authenticated()
  @Get('licensetypes/status')
  getLicenseSourceStatusAsync(
    @QueryParam('licenseTypeId') licenseTypeId: string | null,
    @QueryParam('state') state: string | null,
    @QueryParam('licenseTypeSearchQuery') licenseTypeSearchQuery: string | null,
    @QueryParam('sourceIssue') sourceIssue: string | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number | null,
    @QueryParam('sortedBy') sortedBy: SimplifiedLicenseTypeStatusSort | null,
    @QueryParam('sortDirection') sortDirection: SortDirection
  ): Promise<IPageRes<ISimplifiedLicenseTypeStatus>> {
    // API method will be empty
  }
  //#endregion

  //#region Taxonomies
  @Authenticated()
  @Get('taxonomies/groups')
  getTaxonomyGroupsAsync(
    @QueryParam('sortedBy') sortedBy: TaxonomyGroupSort | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number,
    @QueryParam('sortDirection') sortDirection: SortDirection | null,
    @QueryParam('sort') sort: Sort<TaxonomyGroupSort> | null
  ): Promise<IPageRes<ITaxonomyGroup>> {
    // API method will be empty
  }

  @Authenticated()
  @Get('taxonomies')
  getTaxonomiesAsync(
    @QueryParam('sortedBy') sortedBy: TaxonomySort | null,
    @QueryParam('cursor') cursor: string | null,
    @QueryParam('count') count: number,
    @QueryParam('sortDirection') sortDirection: SortDirection | null,
    @QueryParam('sort') sort: Sort<TaxonomySort> | null
  ): Promise<IPageRes<ITaxonomy>> {
    // API method will be empty
  }
  //#endregion
}

export const apiDefinition = new ApiDefinition();
